import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import ProjectListMembers from "./ProjectMembers/ProjectListMembers";
import { BrieGPTChat, useBrieGPTProjects } from "./useBrieGPTItems";
import { BrieTypeProps, ChatOptions } from "./BrieGPTSidebar";
import { BrieGPTTabs } from "./BrieGPTSidebarElements/Types";
import ProjectSettingsDrawer from "./ProjectSettingsDrawer";
import AppEmptyState from "../../../elements/AppEmptyState";
import AppFieldCard from "../../../elements/AppFieldCard";
import useUpdateBrieGPTUser from "./useUpdateBrieGPTUser";
import useDeleteGPTProject from "./useDeleteGPTProject";
import AppSpinner from "../../../elements/AppSpinner";
import IconButton from "../../../elements/IconButton";
import StrUtils from "../../../utils/StrUtils";
import ProjectOptions from "./ProjectOptions";
import useGPTUser from "./useBrieGPTUser";
import useDeleteGPTChat from "./useDeleteGPTChat";
import ChatSettingsDrawer from "./ChatSettingsDrawer";
import useHover from "../../../hooks/useHover";

type Props = {
  tab: BrieGPTTabs;
  setTab: Dispatch<SetStateAction<BrieGPTTabs>>;
} & BrieTypeProps;

export default function ProjectHome({ tab, setTab, ...brieTypeProps }: Props) {
  const { projects, projectsLoading } = useBrieGPTProjects({
    ...brieTypeProps,
    tab: tab,
  });
  const { deleteProject, deletingProject } = useDeleteGPTProject(tab);
  const [params, setParams] = useSearchParams();
  const projectID = params.get("projectID");
  // GPT User hooks
  const { gptUser } = useGPTUser();
  const { updateGPTUser } = useUpdateBrieGPTUser();
  const { deleteChat, deletingChat } = useDeleteGPTChat(projectID, tab);

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [editProjectID, setEditProjectID] = useState<string | null>(null);
  const [editChatID, setEditChatID] = useState<string | null>(null);

  const project = useMemo(() => {
    if (!projectID || !Array.isArray(projects)) return null;

    return projects.find((p) => p.id === projectID) || null;
  }, [projectID, projects]);

  const pinnedChatsIDs = useMemo(() => {
    return gptUser?.PinnedChats?.map((chat: any) => chat?.Chat?.id) || [];
  }, [gptUser?.PinnedChats]);

  const projectChats = useMemo(() => {
    const chats = project?.Chats || [];
    if (!chats.length) return [];

    return chats.reduce((sortingChats, chat) => {
      // Moving the chat to the top if it's pinned.
      if (pinnedChatsIDs.includes(chat.id)) {
        sortingChats.unshift(chat);
      } else {
        // No pinned chat, just push it to the end.
        sortingChats.push(chat);
      }

      return sortingChats;
    }, [] as BrieGPTChat[]);
  }, [project, pinnedChatsIDs]);

  if (projectsLoading)
    return (
      <div className="tw:w-full tw:flex tw:justify-center tw:items-center">
        <AppSpinner size="lg" />
      </div>
    );

  if (!projectID || !project) return null;

  return (
    <>
      <div className="tw:flex tw:justify-between">
        <div className="tw:flex tw:items-center tw:mb-1">
          <i className="mdi mdi-folder-zip tw:text-2xl tw:text-primary" />
          <h3 className="tw:mb-0 tw:pl-2 tw:text-dark">{project.Name}</h3>
        </div>
        <ProjectOptions
          popoverOpen={popoverOpen}
          setPopoverOpen={setPopoverOpen}
          onOpenSettings={() => setEditProjectID(projectID)}
          onDelete={() => {
            deleteProject({ projectID: projectID, ...brieTypeProps });
          }}
          deleting={deletingProject}
          hide={!!editProjectID}
          placement={"autoVerticalEnd"}
          customComponent={
            <IconButton onClick={undefined} icon="mdi mdi-dots-horizontal" />
          }
        />
      </div>
      <p className="tw:mt-0 tw:mb-2 tw:py-0 tw:text-dark tw:text-sm">
        Start a new chat or select an existing one.
      </p>
      <ProjectListMembers
        project={project}
        tab={tab}
        setTab={setTab}
        itemID={
          brieTypeProps.type === "view"
            ? brieTypeProps.viewID
            : brieTypeProps.recordID
        }
      />
      {!projectChats?.length ? (
        <AppEmptyState
          hideIcon
          title="No Chats"
          description="No chats have been created in this project. Start one using the input below 😁"
        />
      ) : (
        <div className="tw:max-w-2xl">
          {projectChats.map((c) => {
            return (
              <ProjectChat
                onClick={() => {
                  params.set("chatID", c.id);
                  setParams(params);
                }}
                chat={c}
                pinnedChatsIDs={pinnedChatsIDs}
                handlePinChat={() => handlePinChat(c)}
                editChatID={editChatID}
                setEditChatID={setEditChatID}
                deletingChat={deletingChat}
                deleteChat={() =>
                  deleteChat({ chatID: c.id, ...brieTypeProps })
                }
              />
            );
          })}
        </div>
      )}
      <ProjectSettingsDrawer
        open={!!editProjectID}
        onClose={() => setEditProjectID(null)}
        projectID={editProjectID}
        tab={tab}
        {...brieTypeProps}
      />
      <ChatSettingsDrawer
        open={!!editChatID}
        onClose={() => setEditChatID(null)}
        chatID={editChatID}
        projectID={projectID}
        tab={tab}
        {...brieTypeProps}
      />
    </>
  );

  function handlePinChat(chat: BrieGPTChat) {
    updateGPTUser({
      payload: {
        PinnedChat: {
          id: StrUtils.uuidv4(),
          Chat: {
            id: chat.id,
            _RecordName: chat.Name,
            _ModuleAPIName: "BrieGPTChat",
          },
        },
      },
    });
  }
}

function ProjectChat({
  onClick,
  chat,
  pinnedChatsIDs,
  handlePinChat,
  editChatID,
  setEditChatID,
  deletingChat,
  deleteChat,
}: {
  onClick: () => void;
  chat: BrieGPTChat;
  pinnedChatsIDs: string[];
  handlePinChat: () => void;
  editChatID: string | null;
  setEditChatID: Dispatch<SetStateAction<string | null>>;
  deletingChat: boolean;
  deleteChat: () => void;
}) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [hoverRef, isHovered] = useHover<HTMLDivElement>(popoverOpen);

  return (
    <div ref={hoverRef}>
      <AppFieldCard
        onClick={onClick}
        isSelected={false}
        icon={"mdi mdi-chat-processing-outline"}
        name={chat.Name}
        description={""}
        full={true}
        css="tw:border-superlight tw:hover:bg-ultralight tw:text-sm"
        innerCSS="tw:py-1"
        options={
          <div>
            {pinnedChatsIDs.includes(chat.id) && (
              <i
                className="mdi mdi-pin-off tw:px-1 mdi mdi-pin-off tw:cursor-pointer tw:text-lg"
                style={{
                  color: "var(--primary)",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePinChat();
                }}
              />
            )}
            {isHovered && (
              <ChatOptions
                id={chat.id}
                editChatID={editChatID}
                setEditChatID={setEditChatID}
                deletingChat={deletingChat}
                onDelete={() => deleteChat()}
                popoverOpen={popoverOpen}
                setPopoverOpen={setPopoverOpen}
                pinned={pinnedChatsIDs.includes(chat.id)}
                onPin={handlePinChat}
              />
            )}
          </div>
        }
      />
    </div>
  );
}
